import { useEffect, useState, type FormEvent } from "react";
import { Loader2, Lock, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { addMeetingMember, createMeeting, fetchMe, ApiError } from "@/lib/api";

/**
 * Create a recurring meeting **series** (standing config) as the signed-in user. Mirrors
 * NewTopicDialog: a plain create — no seed, no scheduling yet (Phase C). The Private toggle is
 * hidden when Google sign-in isn't configured on the server, since the backend 400s a private
 * meeting in that case (there's no verified identity to gate membership on).
 */
export function NewMeetingDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [authEnabled, setAuthEnabled] = useState(true);
  const [agendaText, setAgendaText] = useState("");
  const [inviteesText, setInviteesText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setIsPrivate(false);
    setAgendaText("");
    setInviteesText("");
    setError(null);
    const ac = new AbortController();
    fetchMe(ac.signal)
      .then((me) => setAuthEnabled(me.authEnabled))
      .catch(() => setAuthEnabled(true)); // fail closed on the toggle — the route would 400 anyway
    return () => ac.abort();
  }, [open]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    const agenda = agendaText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    const invitees = inviteesText
      .split(/[,\n]/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    try {
      const result = await createMeeting({
        title: t,
        is_private: authEnabled ? isPrivate : false,
        agenda,
      });
      // Best-effort invites — a bad address shouldn't roll back the meeting itself.
      await Promise.all(
        invitees.map((email) => addMeetingMember(result.meeting_id, email).catch(() => {})),
      );
      onCreated();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("A meeting with that title already exists.");
      } else {
        setError(err instanceof Error ? err.message : "Could not create meeting");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>New meeting</DialogTitle>
            <DialogDescription>
              A recurring series — start a fresh occurrence each time it runs. Unfinished
              action items and uncovered agenda carry forward automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="meeting-title" className="text-sm font-medium">
                Title
              </label>
              <input
                id="meeting-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Weekly standup"
                autoFocus
                className="w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="meeting-agenda" className="text-sm font-medium">
                Standing agenda <span className="font-normal text-muted">(optional, one per line)</span>
              </label>
              <textarea
                id="meeting-agenda"
                value={agendaText}
                onChange={(e) => setAgendaText(e.target.value)}
                placeholder={"Review action items\nBlockers\nWhat's next"}
                rows={3}
                className="w-full resize-none rounded-md border border-border bg-bg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600"
              />
              <p className="text-xs text-muted">
                Stamped onto every occurrence as coverage items to discuss.
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="meeting-invitees" className="text-sm font-medium">
                Invite others <span className="font-normal text-muted">(optional)</span>
              </label>
              <input
                id="meeting-invitees"
                value={inviteesText}
                onChange={(e) => setInviteesText(e.target.value)}
                placeholder="name@codetogether.com, another@codetogether.com"
                className="w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-brand-600"
              />
            </div>

            {authEnabled ? (
              <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-surface px-3 py-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  className="mt-0.5 size-4 rounded border-border text-brand-600 focus:ring-brand-600"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Lock className="size-3.5 text-muted" />
                    Private
                  </span>
                  <span className="mt-0.5 block text-xs text-muted">
                    Only members you add can see or join any occurrence. You become the owner.
                  </span>
                </span>
              </label>
            ) : null}

            {error ? <p className="text-sm text-red-500">{error}</p> : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !title.trim()}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
              Create meeting
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
